import React, { useState, Suspense, useEffect, useRef } from "react";
import { useInView } from "react-intersection-observer";
import useFadeOnCenter from "./useFadeOnCenter";

/* styles */
import "/styles/modern-normalize.css";
import "/styles/global.css";
import "/styles/components/aideer.css";
import "/styles/utility.css";

const deerFrames = [
  "/images/deerdiagram/0001.webp",
  "/images/deerdiagram/0002.webp",
  "/images/deerdiagram/0003.webp",
  "/images/deerdiagram/0004.webp",
  "/images/deerdiagram/0005.webp",
];

/* venison stew model */

const LazyVenisonstew = React.lazy(() =>
  import("../models/Venisonstew").then((mod) => ({
    default: mod.Venisonstew,
  }))
);

const AiDeerDiagram = ({}) => {
  const [frame, setFrame] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const containerRef = useRef(null);

  const { ref: ref8, inView: inView8 } = useInView({
    triggerOnce: true,
    rootMargin: "200px",
  });

  // Intersection Observer to detect visibility
  useEffect(() => {
    const observer = new window.IntersectionObserver(
      ([entry]) => setIsVisible(entry.isIntersecting),
      { threshold: 0.1 }
    );
    if (containerRef.current) observer.observe(containerRef.current);
    return () => {
      if (containerRef.current) observer.unobserve(containerRef.current);
    };
  }, []);

  // Animation effect only when visible
  useEffect(() => {
    if (!isVisible) return;
    const interval = setInterval(() => {
      setFrame((prev) => (prev + 1) % deerFrames.length);
    }, 1000);
    return () => clearInterval(interval);
  }, [isVisible]);

  const heightRef = useRef();
  const antlersRef = useRef();
  const legsRef = useRef();
  const tailRef = useRef();

  const heightOpacity = useFadeOnCenter(heightRef);
  const antlersOpacity = useFadeOnCenter(antlersRef);
  const legsOpacity = useFadeOnCenter(legsRef);
  const tailOpacity = useFadeOnCenter(tailRef);

  return (
    <>
      <div className="diagram" ref={containerRef}>
        {/* header */}
        <div className="diagram__header-container">
          <h2 className="diagram__header">AI Entity: White-Tailed Deer</h2>
          <h3 className="diagram__subheader-1">
            Odocoileus virginianus (A.I.)
          </h3>
          <h3 className="diagram__subheader-2">Former Occupation: Unknown</h3>
        </div>
        {/* animated deer */}
        <div className="diagram__text-1">
          <h4 className="diagram__text-bold">Habitat and Distribution</h4>
          <p className="diagram__text-regular">
            Found across temperate North America, with a strong preference for
            ephemeral ponds and wetlands. <br /> <br />
            Certain individuals prefer urban environments, like marinas and
            active harbours
          </p>
        </div>
        <img
          className="diagram__animation"
          src={deerFrames[frame]}
          alt="Animated AI Deer"
          loading="lazy"
        />
        <div className="diagram__text-1 diagram__height-drawings-container">
          <h4 className="diagram__text-bold">Key Features</h4>
          {/* height graph*/}
          <div className="diagram__height-drawings">
            <img
              className="diagram__height-drawings-image"
              src="/images/deerdiagram/deerheightgraph.webp"
              alt="height comparison of AI Deer to human and normal deer"
              loading="lazy"
            />
            <img
              className="diagram__height-drawings-image"
              src="/images/deerdiagram/deerheightcharao.webp"
              alt="height comparison of AI Deer to human and normal deer"
              loading="lazy"
              ref={heightRef}
              style={{ opacity: heightOpacity, transition: "opacity 0.1s" }}
            />
          </div>
          <p className="diagram__text-regular">
            Abnormally large, standing 7 - 9 m at shoulder height
          </p>
        </div>
        {/* line drawings */}
        <div className="diagram__line-drawings-container">
          {/* head */}
          <div className="diagram__line-drawings">
            <img
              className="diagram__line-drawings-image flipped"
              src="/images/deerdiagram/head.webp"
              alt="line drawing of AI Deer head"
              loading="lazy"
            />
            <p
              className="diagram__line-drawings-text right"
              ref={antlersRef}
              style={{ opacity: antlersOpacity, transition: "opacity 0.1s" }}
            >
              No individual has been documented with antlers
            </p>
          </div>
          {/* legs */}
          <div className="diagram__line-drawings">
            <p
              className="diagram__line-drawings-text left"
              ref={legsRef}
              style={{ opacity: legsOpacity, transition: "opacity 0.1s" }}
            >
              Limbs glow with a faint blue light, and fade near toes
            </p>
            <img
              className="diagram__line-drawings-image flipped"
              src="/images/deerdiagram/legsfix.webp"
              alt="line drawing of AI Deer legs"
              loading="lazy"
            />
          </div>
          {/* tail */}
          <div className="diagram__line-drawings">
            <img
              className="diagram__line-drawings-image"
              src="/images/deerdiagram/tail.webp"
              alt="line drawing of AI Deer tail"
              loading="lazy"
            />
            <p
              className="diagram__line-drawings-text right"
              ref={tailRef}
              style={{ opacity: tailOpacity, transition: "opacity 0.1s" }}
            >
              Although white on the underside, tail has never been documented in
              “raised position"
            </p>
          </div>
        </div>
        {/* warning */}
        <div className="diagram__text-1 diagram__biometrics-drawing-container">
          <h4 className="diagram__text-bold">Warning</h4>
          <p className="diagram__text-regular">
            Biometric template storage can become compromised within 5 m of
            entity
          </p>

          <div className="diagram__biometrics-drawings">
            <img
              className=".diagram__biometrics-drawing-image diagram__biometrics-drawing-image--black"
              src="/images/deerdiagram/diagrambiohexv2.webp"
              alt="safe radius from AI Deer for biometric template storage"
              loading="lazy"
            />
            <img
              className="diagram__biometrics-drawing-image diagram__biometrics-drawing-image--col"
              src="/images/deerdiagram/diagrambiohexv2col.webp"
              alt="safe radius from AI Deer for biometric template storage"
              loading="lazy"
            />
          </div>
        </div>
        <hr className="diagram__dashed-line" /> {/* <-- Add this line */}
        {/* recipe */}
        <div className="diagram__text-1">
          <h4 className="diagram__text-bold ">Extra Information</h4>
          <p className="diagram__text-regular">
            Please note, hunting season for non-AI deer is in late October to
            December
          </p>
        </div>
        {/* venison stew model */}
        <div className="canvas-container" ref={ref8}>
          {inView8 && (
            <Suspense fallback={<p>Loading venison stew...</p>}>
              <LazyVenisonstew />
            </Suspense>
          )}
        </div>
        <div></div>
      </div>
    </>
  );
};

export default AiDeerDiagram;
